/**
 * NITAQ Design Tokens.
 *
 * **مصدر الحقيقة: `brand/Nitaq_Brand_Book_v1_0.pdf` (Brand Design Freeze).**
 * بنية المقاسات والمسافات تبقى من `design/DESIGN-SYSTEM.md`؛ الألوان والخطوط
 * من الـBrand Book وحده.
 *
 * الألوان الستة المعتمدة فقط:
 *   NITAQ TEAL #00E5C7 · DEEP GRAPHITE #070C10 · GRAPHITE #111820
 *   SLATE #1B232B · FOG #D9DEE3 · WHITE #FFFFFF
 *
 * درجات النص مشتقة بشفافية من FOG — لا ألوان مخترعة خارج اللوحة.
 */

/** لوحة العلامة كما وردت في الـBrand Book. */
export const brand = {
  teal: '#00E5C7',
  deepGraphite: '#070C10',
  graphite: '#111820',
  slate: '#1B232B',
  fog: '#D9DEE3',
  white: '#FFFFFF',
} as const;

export const color = {
  // الأسطح — من لوحة العلامة
  ink900: brand.deepGraphite,
  base800: brand.deepGraphite,
  surface700: brand.graphite,
  panel600: brand.graphite,
  raised500: brand.slate,
  field: brand.deepGraphite,
  hairline: 'rgba(217,222,227,.12)',
  hairlineSoft: 'rgba(217,222,227,.08)',
  hairlineHard: brand.slate,
  sidebar: brand.deepGraphite,

  // النص — WHITE و FOG بدرجات شفافية
  text: brand.white,
  textBody: brand.fog,
  textMuted: 'rgba(217,222,227,.72)',
  textSubtle: 'rgba(217,222,227,.58)',
  label: 'rgba(217,222,227,.45)',
  micro: 'rgba(217,222,227,.38)',
  placeholder: 'rgba(217,222,227,.30)',

  // اللون الرئيسي — لون التوقيع: للإشارة والاختيار، لا لطلاء الواجهة
  teal: brand.teal,
  tealDeep: '#0FB9A5',
  tealSoft: 'rgba(0,229,199,.10)',
  tealFaint: 'rgba(0,229,199,.05)',
  tealBorder: 'rgba(0,229,199,.30)',
  onTeal: '#041412',

  // وظيفية
  green: '#2ED47A',
  red: '#FF5C6C',
  amber: '#F0A24B',
  amberText: '#F0BE84',
  cyan: '#35C6E8',
  /** أرقام الثقة والسيناريوهات — ليست تركوازية أبداً */
  neutralNumber: brand.fog,
} as const;

export const radius = {
  chip: 6,
  button: 10,
  input: 12,
  cardSecondary: 14,
  card: 16,
  hero: 20,
  verdict: 20,
  round: '50%',
} as const;

export const space = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 32, 44, 52] as const;

export const font = {
  /** العربية: Tajawal · اللاتينية: Inter — من الـBrand Book. */
  family: "'Tajawal', 'Inter', system-ui, -apple-system, sans-serif",
  familyLatin: "'Inter', 'Tajawal', system-ui, sans-serif",
  /** الأرقام والقياسات — خط تقني أحادي المسافة يصطف كجدول مسّاح. */
  familyMono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
  display: { size: 56, weight: 600, tracking: '-.03em' },
  displaySub: { size: 32, weight: 300 },
  h1: { size: 28, weight: 600, tracking: '-.02em' },
  heroStatement: { size: 34, weight: 500 },
  kpi: { size: 30, weight: 600 },
  cardNumber: { size: 22, weight: 600 },
  h2: { size: 20, weight: 600 },
  cardTitle: { size: 16, weight: 600 },
  body: { size: 14, weight: 400 },
  secondary: { size: 12.5, weight: 400 },
  label: { size: 11.5, weight: 600 },
  micro: { size: 10.5, weight: 400 },
} as const;

/** نقطة التحوّل الوحيدة المعتمدة. */
export const BREAKPOINT_NAV = 900;

export const shadow = {
  menu: '0 26px 60px rgba(0,0,0,.6)',
  toast: '0 20px 50px rgba(0,0,0,.6)',
  search: '0 22px 50px rgba(0,0,0,.42)',
} as const;

/** ألوان الحكم — تُستخدم في Verdict Chip فقط. */
export const verdictColor = {
  strong: color.green,
  good: color.teal,
  fair: color.textMuted,
  'above-market': color.red,
} as const;

/**
 * تسميات الشارة.
 *
 * ⚠️ هذه الشارة مشتقة من **درجة جاذبية السعر** (OP-1.0)، لا من موقع السعر
 * من نطاق القيمة العادلة. التسميات القديمة («أعلى من السوق») كانت تصف موقعاً
 * لا تقيسه، فتناقض العنوان حين يقع السعر داخل النطاق بينما الدرجة منخفضة.
 * التسميات الآن تصف **قوة الإشارة** التي تقيسها الدرجة فعلاً.
 */
export const verdictLabel = {
  strong: 'إشارة سعرية قوية',
  good: 'إشارة سعرية متوسطة',
  fair: 'إشارة سعرية ضعيفة',
  'above-market': 'لا ميزة سعرية',
} as const;

/** تدرّجات الخريطة الحرارية الخمسة (مطابقة لسطح الخريطة المعتمد). */
export const mapRamp = {
  price: ['#0d2f36', '#155e63', '#12857f', '#0fb9a5', '#00e5c7'],
  opportunity: ['#6b2b2f', '#7c4a33', '#8a6f30', '#3f7d52', '#2ed47a'],
  momentum: ['#12303f', '#17556f', '#1e88a8', '#35c6e8', '#8ceaff'],
  liquidity: ['#3a2a13', '#5c3f16', '#8a611c', '#c08828', '#f0a24b'],
  pressure: ['#ff5c6c', '#9c5457', '#4a5a5c', '#0fb9a5', '#00e5c7'],
} as const;
